import styles from './Spinner.module.css'

export interface SpinnerProps {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Color variant */
  variant?: 'default' | 'gradient' | 'muted'
  /** Optional label for accessibility */
  label?: string
  className?: string
}

const sizes = {
  xs: 16,
  sm: 20,
  md: 28,
  lg: 40,
  xl: 56,
}

export function Spinner({
  size = 'md',
  variant = 'default',
  label = 'Loading',
  className,
}: SpinnerProps) {
  const px = sizes[size]
  const strokeWidth = size === 'xs' || size === 'sm' ? 3 : size === 'md' ? 3 : 2.5
  const r = (px - strokeWidth) / 2
  const circumference = 2 * Math.PI * r
  const dashLength = circumference * 0.7

  return (
    <div
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      role="status"
      aria-label={label}
      style={{ width: px, height: px }}
    >
      <svg
        className={styles.svg}
        width={px}
        height={px}
        viewBox={`0 0 ${px} ${px}`}
      >
        {/* Track ring */}
        <circle
          className={styles.track}
          cx={px / 2}
          cy={px / 2}
          r={r}
          fill="none"
          strokeWidth={strokeWidth}
        />

        {/* Gradient definition */}
        {variant === 'gradient' && (
          <defs>
            <linearGradient id={`spinner-grad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--nexus-purple)" />
              <stop offset="100%" stopColor="var(--nexus-pink)" />
            </linearGradient>
          </defs>
        )}

        {/* Spinning arc */}
        <circle
          className={`${styles.arc} ${styles[variant]}`}
          cx={px / 2}
          cy={px / 2}
          r={r}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${dashLength} ${circumference - dashLength}`}
          stroke={variant === 'gradient' ? `url(#spinner-grad-${size})` : undefined}
        />
      </svg>

      {/* Carousel dots — orbit around the spinner */}
      <div className={styles.dots} style={{ width: px, height: px }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`${styles.dot} ${styles[`dot${i}`]} ${variant === 'gradient' ? styles.dotGradient : variant === 'muted' ? styles.dotMuted : styles.dotDefault}`}
            style={{
              width: size === 'xs' ? 2 : size === 'sm' ? 2.5 : size === 'md' ? 3 : 4,
              height: size === 'xs' ? 2 : size === 'sm' ? 2.5 : size === 'md' ? 3 : 4,
            }}
          />
        ))}
      </div>
    </div>
  )
}
